import { Delete as DeleteIcon, LockOutlined, MoreVert, OpenInNew } from "@mui/icons-material";
import { Avatar, Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { pageDialogOpen } from "../../../state/app";
import { type Organization } from "./types";

const tDeleteOrganizationTooltip = "Opens the delete organization confirmation modal.";
const tDelete = "Delete";
const tNoPublicPage = "No public page";

function getTypeLabel(organization: { isExternal: boolean; organizationType?: string | null }): string {
  const type = organization.organizationType;
  if (type === "SchoolDistrict") {
    return "School District";
  }
  if (type === "School") {
    return "School";
  }
  if (type === "Organization" || organization.isExternal) {
    return "Organization";
  }
  return type ?? "School";
}

interface OrganizationItemProps {
  organization: Organization;
  isSiteAdmin: boolean;
  showNoPublicPageIndicator?: boolean;
  showOpenInNewIcon?: boolean;
  showType?: boolean;
}

const OrganizationItem = ({
  organization,
  isSiteAdmin,
  showNoPublicPageIndicator = false,
  showOpenInNewIcon = false,
  showType = true,
}: OrganizationItemProps) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    handleMenuClose();
    dispatch(
      pageDialogOpen({
        contentProps: { organizationId: organization.id, organizationName: organization.name },
        contentType: "delete-organization",
      }),
    );
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: { md: "1rem", sm: "0.75rem", xs: "0.5rem" },
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: "1 1 auto",
          gap: { md: "1rem", sm: "0.75rem", xs: "0.5rem" },
          minWidth: 0,
        }}
      >
        <Avatar
          alt={organization.name}
          src={organization.avatarUrl}
          sx={{
            height: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
            width: { md: "3.5rem", sm: "3rem", xs: "2.5rem" },
          }}
          slotProps={{
            img: {
              "aria-label": `${organization.name} organization avatar`,
            },
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="p"
            sx={{
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1,
              display: "-webkit-box",
              fontSize: { md: "1.25rem", sm: "1rem", xs: "0.9rem" },
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            variant="h6"
          >
            {organization.name}
          </Typography>
          <Stack spacing={0.5}>
            {showType && (
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: { md: "0.8125rem", sm: "0.75rem", xs: "0.7rem" },
                  minWidth: 0,
                }}
                variant="body2"
              >
                {getTypeLabel(organization)}
              </Typography>
            )}
            <Typography
              sx={{
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 1,
                display: "-webkit-box",
                fontSize: { md: "0.875rem", sm: "0.8125rem", xs: "0.75rem" },
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              variant="body2"
            >
              {organization.address}
            </Typography>
            {organization.description && (
              <Typography
                sx={{
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  color: "text.secondary",
                  display: "-webkit-box",
                  fontSize: { md: "0.875rem", sm: "0.8125rem", xs: "0.75rem" },
                  minWidth: 0,
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  textOverflow: "ellipsis",
                }}
                variant="body2"
              >
                {organization.description}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {showOpenInNewIcon && <OpenInNew sx={{ color: "primary.main", fontSize: "1.25rem" }} />}
        {showNoPublicPageIndicator && (
          <Box
            aria-label={tNoPublicPage}
            sx={{
              alignItems: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.secondary",
              display: "flex",
              flexShrink: 0,
              minWidth: 140,
              px: 1,
              py: 0.5,
            }}
          >
            <LockOutlined sx={{ fontSize: "1rem", mr: 0.5 }} />
            <Typography component="span" variant="caption">
              {tNoPublicPage}
            </Typography>
          </Box>
        )}
        {isSiteAdmin && (
          <Box sx={{ alignItems: "flex-start", display: "flex" }}>
            <IconButton aria-label={tDeleteOrganizationTooltip} onClick={handleMenuClick} size="small">
              <Tooltip placement="top-start" title={tDeleteOrganizationTooltip}>
                <MoreVert fontSize="small" />
              </Tooltip>
            </IconButton>
            <Menu anchorEl={anchorEl} onClick={handleMenuClose} onClose={handleMenuClose} open={Boolean(anchorEl)}>
              <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
                <DeleteIcon sx={{ fontSize: "small", mr: 1 }} />
                {tDelete}
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export { OrganizationItem };
